<script setup lang="ts">
import { JOB_PACK_DAYS } from '~/constants/pricing'

/**
 * How the locked part of an interview prep opens: this one prep on its own,
 * or the Job Search Pack, which has the prep for every posting (and unlocks
 * this one too, see server/api/guest-export/[token].get.ts). Shown under the
 * preview.
 */
withDefaults(defineProps<{
  /** This prep can be bought on its own (it is parked and on sale). */
  canBuySingle: boolean
  /** A checkout is opening. */
  busy?: boolean
  /** Smaller, for the editor's side panel. */
  compact?: boolean
}>(), {
  busy: false,
  compact: false,
})

const emit = defineEmits<{
  'buy-single': []
  'buy-pack': []
}>()

const { t } = useI18n()
const { interviewPrepPrice } = usePremiumPlans()
/** Less what was paid for this CV before, as the checkout charges it (usePackCredit). */
const { packPrice: jobPackPrice, creditNote } = usePackCredit()
</script>

<template>
  <div>
    <div class="grid gap-2" :class="compact ? '' : 'sm:grid-cols-2'">
      <button
        v-if="canBuySingle"
        type="button"
        class="unlock-option"
        :disabled="busy"
        @click.stop="emit('buy-single')"
      >
        <span class="flex items-baseline justify-between gap-3">
          <span class="font-title !text-sm font-semibold !text-ink dark:!text-white">{{ t('interviewPrep.unlock.single') }}</span>
          <span class="shrink-0 font-title !text-base font-bold tabular-nums !text-ink dark:!text-white">{{ interviewPrepPrice }}</span>
        </span>
        <span class="mt-1 block !text-xs !text-muted dark:!text-gray-400">{{ t('interviewPrep.unlock.singleText') }}</span>
      </button>
      <button
        type="button"
        class="unlock-option unlock-option-pack"
        :disabled="busy"
        @click.stop="emit('buy-pack')"
      >
        <span class="flex items-baseline justify-between gap-3">
          <span class="font-title !text-sm font-semibold !text-ink dark:!text-white">{{ t('premium.jobPack.title') }}</span>
          <span class="shrink-0 font-title !text-base font-bold tabular-nums !text-ink dark:!text-white">{{ jobPackPrice }}</span>
        </span>
        <span class="mt-1 block !text-xs !text-muted dark:!text-gray-400">{{ t('interviewPrep.unlock.packText', { days: JOB_PACK_DAYS }) }}</span>
        <span v-if="creditNote" class="mt-1 block !text-xs font-semibold !text-ink dark:!text-white">{{ creditNote }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.unlock-option {
  @apply block w-full rounded-lg border border-line bg-white p-3 text-left transition-colors hover:border-accent disabled:cursor-wait disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800;
}

.unlock-option-pack {
  @apply border-accent/50 bg-accent/5 dark:bg-accent/10;
}
</style>
